import { Component, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { AuditTrailTransactionNotesDistributionItem } from './audit-trail-transaction-notes-distribution-item.model';
import moment from 'moment';
import {
  DetailRowService,
  FilterService,
  GridComponent,
  GridModule,
  PageService,
  SearchService,
  SelectionService,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { ToastComponent } from '../../shared/toast.component';
import { AuditTrailTransactionNotesDistributionService } from './audit-trail-transaction-notes-distribution.service';
import { NGXLogger } from 'ngx-logger';
import { SpinnerComponent } from '../../shared/spinner.component';
import { DateTimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { AsyncPipe, NgClass } from '@angular/common';
import { CanSendAuditTrailTransactionNotesDistributionNotificationComponent } from './can-send-audit-trail-transaction-notes-distribution-notification.component';

@Component({
  selector: 'eaa-audit-trail-transaction-notes-distribution',
  providers: [DetailRowService, FilterService, PageService, SearchService, SelectionService, ToolbarService],
  imports: [
    AsyncPipe,
    CanSendAuditTrailTransactionNotesDistributionNotificationComponent,
    DateTimePickerModule,
    GridModule,
    NgClass,
    SpinnerComponent,
    ToastComponent
  ],
  template: `
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-3">
          <div>
            <h5 class="card-title">TRANSACTION NOTES DISTRIBUTION</h5>
            <h6 class="card-subtitle text-body-secondary">Audit Trail</h6>
          </div>
          @if (loadingPendingCount) {
            <div class="d-flex align-items-center gap-2">
              <eaa-spinner [diameter]="16" />
              <span class="text-muted">Loading files pending distribution...</span>
            </div>
          } @else {
            <span
              class="badge fs-6 px-3 py-2"
              [ngClass]="{
                'text-success-emphasis bg-success-subtle': pendingFilesCount > 0,
                'text-secondary-emphasis bg-secondary-subtle': pendingFilesCount === 0
              }"
            >
              {{ pendingFilesCount }} file{{ pendingFilesCount !== 1 ? 's' : '' }} pending distribution
            </span>
          }
          <button
            type="button"
            class="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
            [disabled]="triggeringDistribution || pendingFilesCount === 0"
            (click)="triggerDistribution()"
          >
            @if (triggeringDistribution) {
              <eaa-spinner />
              <span>DISTRIBUTING...</span>
            } @else {
              <span>START DISTRIBUTION</span>
            }
          </button>
          <eaa-can-send-audit-trail-transaction-notes-distribution-notification>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
              [disabled]="sendingNotificationEmails || selectedItemIds.length === 0"
              (click)="sendNotificationEmails()"
              title="Only selected Success rows with empty Notification Sent are included."
            >
              @if (sendingNotificationEmails) {
                <eaa-spinner style="secondary" />
                <span>SENDING...</span>
              } @else {
                <span>SEND NOTIFICATION EMAILS</span>
              }
            </button>
          </eaa-can-send-audit-trail-transaction-notes-distribution-notification>
        </div>
        <div class="d-flex align-items-center gap-3">
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="Start"
            floatLabelType="Auto"
            [(value)]="start"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          ></ejs-datetimepicker>
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="End"
            floatLabelType="Auto"
            [(value)]="end"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          ></ejs-datetimepicker>
        </div>
      </div>
      <div class="card-body p-0">
        <ejs-grid
          #grdAuditTrailTransactionNotesDistributionItems
          id="grdAuditTrailTransactionNotesDistributionItems"
          [dataSource]="items$ | async"
          [allowPaging]="true"
          [pageSettings]="{ pageSize: 50, pageSizes: ['10', '20', '50', '100', '250', 'All'] }"
          [allowFiltering]="true"
          [filterSettings]="{ enableCaseSensitivity: false, ignoreAccent: true, type: 'Excel' }"
          [allowTextWrap]="true"
          [toolbar]="['Search']"
          [allowSelection]="true"
          [selectionSettings]="{ type: 'Multiple', mode: 'Row', checkboxOnly: true }"
          (rowSelected)="updateSelectedItemIds()"
          (rowDeselected)="updateSelectedItemIds()"
          (actionComplete)="onGridActionComplete()"
        >
          <e-columns>
            <e-column type="checkbox" width="60px" />
            <e-column field="id" headerText="#" width="80px" format="N0" [isPrimaryKey]="true" />
            <e-column
              field="timestamp"
              headerText="Timestamp"
              type="datetime"
              format="dd/MM/yyyy HH:mm:ss zz"
              width="220px"
            />
            <e-column field="transactionId" headerText="Transaction ID" width="160px" />
            <e-column field="version" headerText="Version" width="120px" />
            <e-column field="accountNumber" headerText="Portfolio" width="200px" />
            <e-column field="result" headerText="Result" width="120px">
              <ng-template #template let-data>
                <span
                  class="badge"
                  [ngClass]="{
                    'text-success-emphasis': data.result === 'Success',
                    'bg-success-subtle': data.result === 'Success',
                    'text-warning-emphasis': data.result === 'Skipped',
                    'bg-warning-subtle': data.result === 'Skipped',
                    'text-danger-emphasis': data.result === 'Failed',
                    'bg-danger-subtle': data.result === 'Failed'
                  }"
                >
                  {{ data.result }}
                </span>
              </ng-template>
            </e-column>
            <e-column field="comments" headerText="Comments" width="300px" />
            <e-column
              field="emailNotificationSent"
              headerText="Notification Sent"
              type="datetime"
              format="dd/MM/yyyy HH:mm:ss zz"
              width="220px"
            />
            <e-column field="fileName" headerText="File" />
          </e-columns>
        </ejs-grid>
      </div>
    </div>

    <eaa-toast #toastTriggerResult [showCloseButton]="true">
      <div id="title">TRIGGER DISTRIBUTION</div>
      <div id="content">
        {{ triggerResultMessage }}
      </div>
    </eaa-toast>

    <eaa-toast #toastNotificationResult [showCloseButton]="true">
      <div id="title">SEND NOTIFICATION EMAILS</div>
      <div id="content">
        {{ notificationResultMessage }}
      </div>
    </eaa-toast>
  `
})
export class AuditTrailTransactionNotesDistributionComponent implements OnInit {
  public items$: Observable<AuditTrailTransactionNotesDistributionItem[]> | null = null;
  public today = moment().endOf('day').toDate();
  public searching = false;
  public triggeringDistribution = false;
  public sendingNotificationEmails = false;
  public loadingPendingCount = false;
  public pendingFilesCount = 0;
  public selectedItemIds: number[] = [];
  public start: Date = moment().subtract(1, 'days').toDate();
  public end: Date = moment(this.today).toDate();
  public processingMessage = '';
  public triggerResultMessage = '';
  public notificationResultMessage = '';

  @ViewChild('grdAuditTrailTransactionNotesDistributionItems')
  public grid?: GridComponent;

  @ViewChild('toastTriggerResult')
  public toastTriggerResult?: ToastComponent;

  @ViewChild('toastNotificationResult')
  public toastNotificationResult?: ToastComponent;

  constructor(
    private auditTrailTransactionNotesDistributionService: AuditTrailTransactionNotesDistributionService,
    private logger: NGXLogger
  ) {}

  public ngOnInit(): void {
    this.setAuditTrailItems();
    this.loadPendingFilesCount();
  }

  public dateRangeSelected(): void {
    this.searching = true;

    this.setAuditTrailItems();

    setTimeout(() => {
      this.searching = false;
    }, 500);
  }

  public loadPendingFilesCount(): void {
    this.loadingPendingCount = true;

    this.auditTrailTransactionNotesDistributionService.countTransactionNotesFilesPendingDistribution$().subscribe({
      next: (response) => {
        this.loadingPendingCount = false;
        if (response.count !== undefined) {
          this.pendingFilesCount = response.count;
        } else if (response.error) {
          this.logger.error('Error loading pending files count:', response.error);
          this.pendingFilesCount = 0;
        }
      },
      error: (error) => {
        this.logger.error('Failed to load pending files count', error);
        this.loadingPendingCount = false;
        this.pendingFilesCount = 0;
      }
    });
  }

  public triggerDistribution(): void {
    this.triggeringDistribution = true;
    this.logger.log('Triggering transaction notes distribution...');

    this.auditTrailTransactionNotesDistributionService.triggerTransactionNotesDistribution$().subscribe({
      next: (response) => {
        this.logger.log('Transaction notes distribution triggered successfully', response);
        this.triggeringDistribution = false;
        this.triggerResultMessage = response.message;
        this.toastTriggerResult?.show();
        // Refresh the audit trail items and pending count after successful trigger
        this.setAuditTrailItems();
        this.loadPendingFilesCount();
      },
      error: (error) => {
        this.logger.error('Failed to trigger transaction notes distribution', error);
        this.triggeringDistribution = false;
      }
    });
  }

  public updateSelectedItemIds(): void {
    const selectedRows = (this.grid?.getSelectedRecords() ?? []) as AuditTrailTransactionNotesDistributionItem[];

    this.selectedItemIds = selectedRows
      .filter((item) => this.isEligibleForNotificationEmail(item))
      .map((item) => item.id);
  }

  public onGridActionComplete(): void {
    this.updateSelectedItemIds();
  }

  public sendNotificationEmails(): void {
    if (this.selectedItemIds.length === 0) {
      return;
    }

    this.sendingNotificationEmails = true;
    const selectedIds = [...this.selectedItemIds];

    this.auditTrailTransactionNotesDistributionService
      .sendNotificationEmailForTransactionNoteDistributionItem$(selectedIds)
      .subscribe({
        next: (updatedItems) => {
          const updatedCount = Array.isArray(updatedItems) ? updatedItems.length : selectedIds.length;
          this.notificationResultMessage = `Notification emails sent for ${updatedCount} item${updatedCount !== 1 ? 's' : ''}.`;
          this.toastNotificationResult?.show();
          this.sendingNotificationEmails = false;
          this.selectedItemIds = [];
          this.grid?.clearSelection();
          this.setAuditTrailItems();
        },
        error: (error) => {
          this.logger.error('Failed to send notification emails', error);
          this.sendingNotificationEmails = false;
        }
      });
  }

  private setAuditTrailItems() {
    this.items$ = this.auditTrailTransactionNotesDistributionService.getAuditTrailTransactionNotesDistributionItems$(
      this.start,
      this.end,
      null
    );
  }

  private isEligibleForNotificationEmail(item: AuditTrailTransactionNotesDistributionItem | null | undefined): boolean {
    return !!item && item.result === 'Success' && !item.emailNotificationSent;
  }
}
